import { Injectable } from '@angular/core';
import { AlertController, ModalController, Platform } from '@ionic/angular';
import { AppService } from './app.service';
import { BehaviorSubject } from 'rxjs';
import { EncryptionService } from './encryption.service';
import { AppEnum } from '../appenum';
import { DeviceDetectorService } from 'ngx-device-detector';
import { environment } from 'src/environments/environment';
import { wrapAllMethodsWithLogging } from '../core/decorators/log-all-errors.decorator';
import { LoggingService } from './logging.service';

@Injectable({
  providedIn: 'root'
})
export class PermissionService {
  private permissionDeniedSubject = new BehaviorSubject<boolean>(false);
  public permissionDenied$ = this.permissionDeniedSubject.asObservable();
  watchId: number | null = null; // Holds the ID for geolocation watch
  locationCheckInterval: any = null; // Holds the interval for checking GPS status
  permissionStatusRef: PermissionStatus | null = null;
  componentName: string = 'permission-service';
  constructor(public alertController: AlertController,
    public _appservice: AppService,
    public encryptionService: EncryptionService,
    public _appenum: AppEnum,
    public deviceService: DeviceDetectorService,
    private modalCtrl: ModalController,
    public platformService: Platform,
    public loggingService: LoggingService,
  ) {
    wrapAllMethodsWithLogging(this, this.componentName);
  }

  /**
   * Checks the geolocation permission status using the Permissions API.
   * If permission is granted, it attempts to retrieve the location.
   * If permission is denied or needs to be requested, it updates the UI accordingly.
   */
  async checkGeolocationPermission(isClick = false) {
    var deviceInfo = this.deviceService.getDeviceInfo();
    const browserName = deviceInfo.browser?.toLowerCase() || '';
    const browserVersion = parseInt(deviceInfo.browser_version?.split('.')[0], 10); // → 140
    if (!navigator || !navigator.permissions) {
      console.warn("Browser does not support Permissions API or navigator object is missing.");
      this.logGeolocationError("Permissions API not supported For this Browser");
      try {
        navigator.geolocation.getCurrentPosition(
          (pos) => {
            this.handlePermissionState("granted", isClick);
          }, (err) => {
            const isOutdated =
              (browserName === 'chrome' && browserVersion < 43) ||
              (browserName === 'safari' && browserVersion < 16) ||
              (browserName === 'firefox' && browserVersion < 46);
            if (isOutdated) {
              this._appservice.ionicCustomPromptForCors('Warning',
                `Your ${deviceInfo.browser} browser version (${deviceInfo.browser_version}) is outdated.\n\n` +
                `Please update your browser for the best experience.`
              );
            }
            this.UpdateUserActivityOnPermission(err.message)
            if (!navigator || !navigator.permissions) {

            } else {
              this.updatePermissionStatus(true);
            } // this.handlePermissionState("denied", isClick);
          },
          { enableHighAccuracy: false, timeout: 20000, maximumAge: 10000 }
        );
      } catch (e) {
        this.logGeolocationError("Exception in fallback geolocation", "", "", null, "", String(e));
        this.handlePermissionState("denied", isClick);
      }
      return;
    }

    try {
      const permissionStatus = await navigator.permissions.query({ name: "geolocation" });
      if (!permissionStatus) {
        this.logGeolocationError("Failed to retrieve geolocation permission status");
        return;
      }
      setTimeout(() => {
        this.handlePermissionState(permissionStatus.state, isClick);
      }, 3000)
      // Manage permission status change listener
      if (this.permissionStatusRef) this.permissionStatusRef.onchange = null;
      this.permissionStatusRef = permissionStatus;
      this.permissionStatusRef.onchange = () => {
        this.handlePermissionState(permissionStatus.state, isClick);
      };
    } catch (error) {
      console.error("Error checking geolocation permission:", error);
      this.logGeolocationError("Error while checking geolocation permission", '', '', null, '', String(error));
    }
  }

  logGeolocationError(
    message: string,
    method: string = '',
    url: string = '',
    payload: any = null,
    responseCode: string = '',
    responseMsg: string = '',
    result: any = '',
    taskDetail: any = ''
  ) {
    const paramRequest = {
      httpRequest: {
        method,
        url,
        payLoad: payload,
        response: {
          responseCode,
          responseMsg,
          result
        }
      },
      taskDetails: {
        TaskId: this.encryptionService.decrypt(this.encryptionService.localstorageDecrypt(this._appenum.EntityOfLocalStorageKeys.taskid))
      }
    };

    this._appservice.logsActivity(
      this._appenum.EntityOfLogLevel.Error,
      message,
      'Permission-service',
      paramRequest.httpRequest,
      paramRequest.taskDetails
    );
  }

  /**
   * Handles different geolocation permission states and takes appropriate actions.
   */
  handlePermissionState(state: PermissionState, isClick: boolean) {
    switch (state) {
      case "granted":
        this.UpdateUserActivityOnPermission('Permission Granted By User');
        this.getUserLocation(isClick);
        break;
      case "prompt":
        try {
          this.requestUserLocation();
          if (!this.platformService.is("ios")) {
            this.updatePermissionStatus(true);
            this.restartLocationCheck();
          }
        } catch (err) {
          console.error("Error while handling 'prompt' permission:", err);
          this.logGeolocationError("Error handling prompt state", "GET", "handlePermissionState", null, "prompt", String(err));
        }
        break;
      default:
        this.UpdateUserActivityOnPermission('Permission Denied By User');
        this.logGeolocationError("Permission denied or unknown state", 'GET', 'handlePermissionState', null, state);
        this.updatePermissionStatus(true);
        this.restartLocationCheck();
        this._appservice.openModel(4);
        break;
    }
  }


  async UpdateUserActivityOnPermission(message = null) {
    var postJson = {
      "taskId": this.encryptionService.decrypt(
        this.encryptionService.localstorageDecrypt(
          this._appenum.EntityOfLocalStorageKeys.taskid
        )
      ),
      "userType": "User",
      "userId": this.encryptionService.decrypt(this.encryptionService.localstorageDecrypt(this._appenum.EntityOfLocalStorageKeys.userid)),
      "taskMode": "INSP2",
      "activity": message
    };
    (await this._appservice.postData(environment.endpoint.createTaskUserActivity, postJson)).subscribe(_res => {
      if (_res.StatusCode == 200) {

      } else {
        // this._appservice.presentToast(_res.Message[this._appSetting.selectedLanguage] != undefined ? _res.Message[this._appSetting.selectedLanguage] : _res.Message[this.encryptionService.decrypt(this.encryptionService.localstorageDecrypt(this._appenum.EntityOfLocalStorageKeys.defaultLanguageSelected))]);
      }
    }, (err) => {
      console.log("err", err)
    })
  }

  /**
   * Starts watching the user's location and handles errors if any.
   */
  async getUserLocation(isClick = false) {
    if (!navigator.geolocation) {
      this.logGeolocationError("Geolocation API not supported", "GET", "getUserLocation");
      this.updatePermissionStatus(true);
      return;
    }

    if (this.watchId) return; // Prevent multiple watch instances


    try {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          this.storeLocation(position);
          this.closeModel();
          this.updatePermissionStatus(false);
        },
        (error) => {
          console.error("Error getting current position:", error);
          this.UpdateUserActivityOnPermission(error.message)
          this.logGeolocationError("Error getting current position", "GET", "getUserLocation", null, "", JSON.stringify(error));
          this._appservice.openModel(4);
          this.updatePermissionStatus(true);
          this.handleLocationError(error, isClick);
        },
        { enableHighAccuracy: true, timeout: 20000, maximumAge: 0 }
      );

      this.watchId = navigator.geolocation.watchPosition(
        (position) => {
          this.updatePermissionStatus(false);
          this.storeLocation(position);
        }, (error) => {
          this.logGeolocationError("Error from watchPosition", "GET", "getUserLocation", null, "", JSON.stringify(error));
          this.checkGeolocationPermission()
          // if (!navigator || !navigator.permissions) {

          // } else {
          //   this.updatePermissionStatus(true);
          //   this.handleLocationError(error, isClick);
          // }

        },
        { enableHighAccuracy: false, timeout: 20000, maximumAge: 10000 }
      );
      this.startCheckingLocation(); // Optional: periodic GPS/accuracy validation
    } catch (err) {
      console.error("Unexpected exception in getUserLocation:", err);
      this.logGeolocationError("Unexpected exception in getUserLocation", "GET", "getUserLocation", null, "", String(err));
      this.updatePermissionStatus(true);
    }
  }

  /**
   * Stores the retrieved location in local storage.
  */

  storeLocation(position: any) {
    if (this.handleInvalidPosition(position)) return;
    this.encryptionService.localstorageEncrypt(this._appenum.EntityOfLocalStorageKeys.latitude, position.coords.latitude);
    this.encryptionService.localstorageEncrypt(this._appenum.EntityOfLocalStorageKeys.longitude, position.coords.longitude);
    this.encryptionService.localstorageEncrypt(this._appenum.EntityOfLocalStorageKeys.accuracy, position.coords.accuracy);
  }
  handleInvalidPosition(position: any): boolean {
    if (!position?.coords || !position.coords.latitude || !position.coords.longitude) {

      this.updatePermissionStatus(true);
      this._appservice.openModel(4);
      this._appservice.presentToast('Unable to fetch latitude longitude. Please try later.');
      const postJson = {
        taskId: this.encryptionService.decrypt(
          this.encryptionService.localstorageDecrypt(
            this._appenum.EntityOfLocalStorageKeys.taskid
          )
        )
      };

      const paramRequest = {
        httpRequest: {
          method: '',
          url: '',
          payLoad: postJson,
          response: {
            responseCode: '',
            responseMsg: '',
            result: position
          }
        },
        taskDetails: ''
      };

      this._appservice.logsActivity(
        this._appenum.EntityOfLogLevel.Error,
        'GPS Granted but Lat/Long Not Found',
        'Permission-service',
        paramRequest.httpRequest,
        paramRequest.taskDetails
      );
      return true; // invalid position
    }

    return false; // valid position
  }

  /**
   * Starts checking if the GPS is turned on/off every 5 seconds.
   */
  startCheckingLocation() {
    if (this.locationCheckInterval) return;
    this.locationCheckInterval = setInterval(async () => {
      try {
        const permissionStatus = await navigator.permissions.query({ name: "geolocation" });
        if (permissionStatus.state === "denied") {
          this.UpdateUserActivityOnPermission('Permission Denied By User');
          this.stopWatchingLocation();
          this.restartLocationCheck();
        } else if (permissionStatus.state === "prompt") {
          // this.updatePermissionStatus(true);
          navigator.geolocation.getCurrentPosition(
            (position) => {
              this.storeLocation(position);
              this.closeModel();
              this.updatePermissionStatus(false);
            },
            (error) => this.handleLocationError(error, false),
            { enableHighAccuracy: true, timeout: 20000, maximumAge: 0 }
          );
        }
      } catch (error) {
        console.error("Error checking geolocation permission:", error);
      }
    }, 5000);
  }

  /**
   * Stops watching the user's location and clears the periodic GPS status check.
   */
  stopWatchingLocation() {
    this.updatePermissionStatus(true);

    if (this.watchId !== null) {
      navigator.geolocation.clearWatch(this.watchId);
      this.watchId = null;
    }
    if (this.locationCheckInterval !== null) {
      clearInterval(this.locationCheckInterval);
      this.locationCheckInterval = null;
    }
  }

  /**
   * Restarts location permission check if denied.
   */
  restartLocationCheck() {
    this.stopWatchingLocation();
    setTimeout(() => this.startCheckingLocation(), 3000);
  }

  /**
   * Explicitly requests the user's location to trigger the permission prompt.
   */
  requestUserLocation() {
    this.updatePermissionStatus(true);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        this.storeLocation(position);
        this.updatePermissionStatus(false);
      },
      (error) => {
        this.updatePermissionStatus(true);
        this.UpdateUserActivityOnPermission(error.message);
        this._appservice.openModel(4);
        console.error("❌ Location error:", error);
      },
      { enableHighAccuracy: true, timeout: 20000, maximumAge: 0 }
    );
  }

  /**
   * Handles location-related errors and takes appropriate actions.
   */
  handleLocationError(error: any, isClick: boolean) {
    switch (error.code) {
      case error.PERMISSION_DENIED:
        this.logGeolocationError("Permission denied by user", 'GET', 'navigator.geolocation.getCurrentPosition', null, String(error.code), error.message);
        this.updatePermissionStatus(true);
        break;
      case error.POSITION_UNAVAILABLE:
        this.logGeolocationError("GPS is disabled or unavailable", 'GET', 'navigator.geolocation.getCurrentPosition', null, String(error.code), error.message);
        this.updatePermissionStatus(true);
        break;
      case error.TIMEOUT:
        this.logGeolocationError("Location request timed out", 'GET', 'navigator.geolocation.getCurrentPosition', null, String(error.code), error.message);
        this.updatePermissionStatus(true);
        if (isClick) this._appservice.openModel(3);
        break;
      default:
        this.logGeolocationError("Unknown location error", 'GET', 'navigator.geolocation.getCurrentPosition', null, String(error.code), error.message);
        this.updatePermissionStatus(true);
        break;
    }
  }

  /**
   * Updates the permission status, typically used for UI updates.
   */
  updatePermissionStatus(status: boolean) {
    this.permissionDeniedSubject.next(status);
  }

  /**
   * Closes the permission modal if open.
   */
  async closeModel() {
    const topModal = await this.modalCtrl.getTop();
    if (topModal) {
      this.modalCtrl.dismiss({}, "", "permissionModel");
    }
  }
}
